
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Bell, ArrowLeft, Save, Send } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";

const NotificationSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    adminEmailAlerts: true,
    userWelcomeEmail: true,
    courseCompletionEmail: true,
    certificateEmail: true,
    smtpServer: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUsername: '',
    fromEmail: 'noreply@neoprismacademy.com',
    fromName: 'Neo Prism Academy Platform'
  });

  const handleSave = () => {
    toast.success("Notification settings saved successfully");
  };

  const handleTestEmail = () => {
    toast.success("Test email sent successfully");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')}>
          <ArrowLeft className="h-4 w-4" />
          Back to System Settings
        </Button>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">Notification Settings</h1>
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Notification Types</CardTitle>
            <CardDescription>Enable or disable different types of notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">Send notifications via email</p>
              </div>
              <Switch
                checked={settings.emailNotifications}
                onCheckedChange={(checked) => setSettings({...settings, emailNotifications: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">SMS Notifications</Label>
                <p className="text-sm text-muted-foreground">Send notifications via SMS</p>
              </div>
              <Switch
                checked={settings.smsNotifications}
                onCheckedChange={(checked) => setSettings({...settings, smsNotifications: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Push Notifications</Label>
                <p className="text-sm text-muted-foreground">Send browser push notifications</p>
              </div>
              <Switch
                checked={settings.pushNotifications}
                onCheckedChange={(checked) => setSettings({...settings, pushNotifications: checked})}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Email Templates</CardTitle>
            <CardDescription>Configure automated email notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>User Welcome Email</Label>
              <Switch
                checked={settings.userWelcomeEmail}
                onCheckedChange={(checked) => setSettings({...settings, userWelcomeEmail: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Course Completion Email</Label>
              <Switch
                checked={settings.courseCompletionEmail}
                onCheckedChange={(checked) => setSettings({...settings, courseCompletionEmail: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Certificate Email</Label>
              <Switch
                checked={settings.certificateEmail}
                onCheckedChange={(checked) => setSettings({...settings, certificateEmail: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Admin Email Alerts</Label>
              <Switch
                checked={settings.adminEmailAlerts}
                onCheckedChange={(checked) => setSettings({...settings, adminEmailAlerts: checked})}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>SMTP Configuration</CardTitle>
            <CardDescription>Configure email server settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smtpServer">SMTP Server</Label>
                <Input
                  id="smtpServer"
                  value={settings.smtpServer}
                  onChange={(e) => setSettings({...settings, smtpServer: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="smtpPort">SMTP Port</Label>
                <Input
                  id="smtpPort"
                  type="number"
                  value={settings.smtpPort}
                  onChange={(e) => setSettings({...settings, smtpPort: parseInt(e.target.value)})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fromEmail">From Email</Label>
                <Input
                  id="fromEmail"
                  type="email"
                  value={settings.fromEmail}
                  onChange={(e) => setSettings({...settings, fromEmail: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="fromName">From Name</Label>
                <Input
                  id="fromName"
                  value={settings.fromName}
                  onChange={(e) => setSettings({...settings, fromName: e.target.value})}
                />
              </div>
            </div>
            
            <Button variant="outline" onClick={handleTestEmail} className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              Send Test Email
            </Button>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
